import { useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Upload,
  FileSpreadsheet,
  Download,
  X,
  CheckCircle2,
  AlertTriangle,
  XCircle,
} from 'lucide-react'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'
import { api, extractApiError, isPlanLimitError } from '../lib/api'
import { memberImportPreview, memberImportResult } from '../lib/types'

const ACCEPT = '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
const MAX_BYTES = 5 * 1024 * 1024

// Row / result status → chip styling (semantic tokens, dark-mode safe).
const STATUS = {
  valid: { label: 'Ready', tone: 'success', Icon: CheckCircle2 },
  duplicate: { label: 'Already a member', tone: 'warning', Icon: AlertTriangle },
  error: { label: 'Needs fixing', tone: 'danger', Icon: XCircle },
  created: { label: 'Created', tone: 'success', Icon: CheckCircle2 },
  skipped: { label: 'Skipped', tone: 'warning', Icon: AlertTriangle },
  failed: { label: 'Failed', tone: 'danger', Icon: XCircle },
}

const ROLE_LABEL = { user: 'User', sub_admin: 'Sub Admin' }

function StatusBadge({ kind }) {
  const s = STATUS[kind] ?? { label: kind, tone: 'accent', Icon: null }
  const Icon = s.Icon
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap"
      style={{
        color: `var(--color-${s.tone})`,
        background: `var(--color-${s.tone}-bg)`,
        borderColor: `var(--color-${s.tone}-border)`,
      }}
    >
      {Icon ? <Icon size={12} /> : null}
      {s.label}
    </span>
  )
}

function Stat({ label, value, tone }) {
  return (
    <div
      className="flex-1 rounded-lg border px-3 py-2 text-center"
      style={
        tone
          ? { borderColor: `var(--color-${tone}-border)`, background: `var(--color-${tone}-bg)` }
          : { borderColor: 'var(--color-rule)' }
      }
    >
      <div
        className="text-xl font-semibold tabular-nums"
        style={{ color: tone ? `var(--color-${tone})` : 'var(--color-ink-strong)' }}
      >
        {value}
      </div>
      <div className="mt-0.5 text-xs text-[color:var(--color-ink-muted)]">{label}</div>
    </div>
  )
}

function downloadCsv(filename, csv) {
  // UTF-8 BOM so Excel opens it with the right encoding.
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export function MemberImportDialog({ open, onClose, onDone }) {
  const fileInputRef = useRef(null)
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [result, setResult] = useState(null)
  const [dragOver, setDragOver] = useState(false)
  const [downloading, setDownloading] = useState(false)

  useEffect(() => {
    if (open) {
      setFile(null)
      setPreview(null)
      setResult(null)
      setDragOver(false)
    }
  }, [open])

  const previewMutation = useMutation({
    mutationFn: async (f) => {
      const fd = new FormData()
      fd.append('file', f)
      const res = await api.post('/users/import/preview', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60_000,
      })
      return memberImportPreview.parse(res.data)
    },
    onSuccess: (data) => setPreview(data),
    onError: (err) => {
      // Bad file (wrong type, no header row, too many rows) — drop it so the
      // dropzone comes back, and show the server's specific reason.
      setFile(null)
      setPreview(null)
      toast.error(extractApiError(err).message)
    },
  })

  const commitMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Choose a file first.')
      const fd = new FormData()
      fd.append('file', file)
      const res = await api.post('/users/import/commit', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120_000,
      })
      return memberImportResult.parse(res.data)
    },
    onSuccess: (data) => {
      setResult(data)
      if (data.created > 0) {
        toast.success(`${data.created} member${data.created === 1 ? '' : 's'} imported.`)
        onDone?.()
      } else {
        toast.error('No members were imported.')
      }
    },
    onError: (err) => {
      if (isPlanLimitError(err)) return
      toast.error(extractApiError(err).message)
    },
  })

  const busy = previewMutation.isPending || commitMutation.isPending

  async function downloadTemplate() {
    try {
      setDownloading(true)
      const res = await api.get('/users/import/template', { responseType: 'blob' })
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = url
      a.download = 'member-import-template.xlsx'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      toast.error(extractApiError(err).message)
    } finally {
      setDownloading(false)
    }
  }

  function pickFile(picked) {
    if (!picked) return
    if (!/\.xlsx$/i.test(picked.name)) {
      toast.error('Only Excel (.xlsx) files are supported.')
      return
    }
    if (picked.size > MAX_BYTES) {
      toast.error('File must be 5 MB or smaller.')
      return
    }
    setResult(null)
    setPreview(null)
    setFile(picked)
    previewMutation.mutate(picked)
  }

  function reset() {
    setFile(null)
    setPreview(null)
    setResult(null)
  }

  function downloadReport() {
    if (!result) return
    const esc = (s) => {
      let v = String(s ?? '')
      // Neutralise spreadsheet formula injection: a cell that starts with
      // = + - @ (or a control char) can execute when the report is opened in
      // Excel. Error rows echo the original typed value, so guard it here.
      if (/^[=+\-@\t\r]/.test(v)) v = `'${v}`
      return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v
    }
    const rows = [
      'Row,Full Name,Email ID,Status,Reason',
      ...result.results.map((r) =>
        [r.rowNumber, r.fullName, r.email, r.status, r.reason].map(esc).join(','),
      ),
    ]
    downloadCsv('member-import-results.csv', rows.join('\n'))
  }

  // ── footer (varies by step) ──
  let footer
  if (result) {
    footer = (
      <>
        <Button variant="secondary" onClick={downloadReport}>
          <Download size={14} />
          Download Report
        </Button>
        <Button onClick={onClose}>Done</Button>
      </>
    )
  } else if (preview) {
    const willImport = preview.summary.seat.willImport
    footer = (
      <>
        <Button variant="secondary" onClick={reset} disabled={busy}>
          Choose Another File
        </Button>
        <Button onClick={() => commitMutation.mutate()} loading={commitMutation.isPending} disabled={busy || willImport < 1}>
          Import {willImport} member{willImport === 1 ? '' : 's'}
        </Button>
      </>
    )
  } else {
    footer = (
      <Button variant="secondary" onClick={onClose} disabled={busy}>
        Cancel
      </Button>
    )
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Import Members"
      description="Download the template, fill in your members, then upload it to add them all at once."
      size="xl"
      footer={footer}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        onChange={(e) => {
          pickFile(e.target.files?.[0] ?? null)
          e.target.value = ''
        }}
        className="hidden"
      />

      {/* ── STEP 3: results ── */}
      {result ? (
        <div className="flex flex-col gap-4">
          <div className="flex gap-3">
            <Stat label="Created" value={result.created} tone={result.created > 0 ? 'success' : undefined} />
            <Stat label="Skipped" value={result.skipped} tone={result.skipped > 0 ? 'warning' : undefined} />
            <Stat label="Failed" value={result.failed} tone={result.failed > 0 ? 'danger' : undefined} />
          </div>
          <p className="text-sm text-[color:var(--color-ink-muted)]">
            {result.created > 0
              ? `Imported members have been sent an invitation to set their password and join.`
              : `No members were created — review the reasons below.`}
          </p>
          <ResultsTable results={result.results} />
        </div>
      ) : preview ? (
        /* ── STEP 2: preview ── */
        <div className="flex flex-col gap-4">
          <div className="flex gap-3">
            <Stat label="Ready to import" value={preview.summary.valid} tone={preview.summary.valid > 0 ? 'success' : undefined} />
            <Stat label="Already members" value={preview.summary.duplicates} tone={preview.summary.duplicates > 0 ? 'warning' : undefined} />
            <Stat label="Need fixing" value={preview.summary.errors} tone={preview.summary.errors > 0 ? 'danger' : undefined} />
          </div>

          {preview.summary.seat.willSkipForSeats > 0 ? (
            <div
              className="flex items-start gap-2 rounded-lg border px-3 py-2 text-sm"
              style={{
                color: 'var(--color-warning)',
                background: 'var(--color-warning-bg)',
                borderColor: 'var(--color-warning-border)',
              }}
            >
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>
                Your plan has {preview.summary.seat.remaining} seat
                {preview.summary.seat.remaining === 1 ? '' : 's'} left. The first{' '}
                {preview.summary.seat.willImport} ready member
                {preview.summary.seat.willImport === 1 ? '' : 's'} will be imported; the remaining{' '}
                {preview.summary.seat.willSkipForSeats} will be skipped. Upgrade your plan to add more.
              </span>
            </div>
          ) : null}

          <PreviewTable rows={preview.rows} />

          <p className="text-xs text-[color:var(--color-ink-muted)]">
            {preview.fileName} · {preview.summary.total} row{preview.summary.total === 1 ? '' : 's'}
            {preview.summary.seat.remaining != null
              ? ` · ${preview.summary.seat.used}/${preview.summary.seat.limit} seats used`
              : ''}
          </p>
        </div>
      ) : (
        /* ── STEP 1: download + upload ── */
        <div className="flex flex-col gap-5">
          <div>
            <p className="text-sm font-medium text-[color:var(--color-ink-strong)]">
              1. Download the template
            </p>
            <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
              The Excel template has dropdowns for Role, Work Role and Department, plus a
              reference sheet with your exact names.
            </p>
            <div className="mt-3">
              <Button variant="secondary" onClick={() => downloadTemplate()} loading={downloading}>
                <FileSpreadsheet size={14} />
                Download Excel Template
              </Button>
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-[color:var(--color-ink-strong)]">2. Upload your file</p>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault()
                setDragOver(true)
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault()
                setDragOver(false)
                pickFile(e.dataTransfer.files?.[0] ?? null)
              }}
              disabled={previewMutation.isPending}
              className={
                'mt-3 w-full rounded-lg border-2 border-dashed p-8 flex flex-col items-center justify-center gap-3 transition-colors cursor-pointer disabled:cursor-wait ' +
                (dragOver
                  ? 'border-[color:var(--color-accent)] bg-[color:var(--color-accent-bg)]'
                  : 'border-[color:var(--color-rule)] hover:border-[color:var(--color-accent)] hover:bg-[color:var(--color-card-hover)]')
              }
            >
              <span className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent)]">
                <Upload size={20} />
              </span>
              <div className="text-center">
                <p className="text-sm font-medium text-[color:var(--color-ink-strong)]">
                  {previewMutation.isPending ? 'Checking your file…' : 'Click to choose a file or drop it here'}
                </p>
                <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
                  Accepts Excel (.xlsx) · up to 5 MB · 200 members per file
                </p>
              </div>
            </button>
          </div>
        </div>
      )}
    </Modal>
  )
}

function PreviewTable({ rows }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-[color:var(--color-rule)]">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-[color:var(--color-ink-muted)] bg-[color:var(--color-canvas)]">
            <th className="px-3 py-2 font-medium">Row</th>
            <th className="px-3 py-2 font-medium">Full Name</th>
            <th className="px-3 py-2 font-medium">Email ID</th>
            <th className="px-3 py-2 font-medium">Role</th>
            <th className="px-3 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium">Details</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.rowNumber} className="border-t border-[color:var(--color-rule)] align-top">
              <td className="px-3 py-2 tabular-nums text-[color:var(--color-ink-muted)]">{r.rowNumber}</td>
              <td className="px-3 py-2 text-[color:var(--color-ink-strong)]">{r.fullName || '—'}</td>
              <td className="px-3 py-2 break-all text-[color:var(--color-ink)]">{r.email || '—'}</td>
              <td className="px-3 py-2 text-[color:var(--color-ink)]">{ROLE_LABEL[r.role] ?? '—'}</td>
              <td className="px-3 py-2"><StatusBadge kind={r.status} /></td>
              <td className="px-3 py-2 text-xs text-[color:var(--color-ink-muted)]">
                {r.messages.length > 0
                  ? r.messages.map((m, i) => <div key={i}>{m}</div>)
                  : [r.workRoleName, r.workspaceName, r.departmentName].filter(Boolean).join(' · ') || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ResultsTable({ results }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-[color:var(--color-rule)]">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-[color:var(--color-ink-muted)] bg-[color:var(--color-canvas)]">
            <th className="px-3 py-2 font-medium">Row</th>
            <th className="px-3 py-2 font-medium">Full Name</th>
            <th className="px-3 py-2 font-medium">Email ID</th>
            <th className="px-3 py-2 font-medium">Result</th>
            <th className="px-3 py-2 font-medium">Reason</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr key={r.rowNumber} className="border-t border-[color:var(--color-rule)] align-top">
              <td className="px-3 py-2 tabular-nums text-[color:var(--color-ink-muted)]">{r.rowNumber}</td>
              <td className="px-3 py-2 text-[color:var(--color-ink-strong)]">{r.fullName || '—'}</td>
              <td className="px-3 py-2 break-all text-[color:var(--color-ink)]">{r.email || '—'}</td>
              <td className="px-3 py-2"><StatusBadge kind={r.status} /></td>
              <td className="px-3 py-2 text-xs text-[color:var(--color-ink-muted)]">{r.reason || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
